import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KnowledgeBaseTable = ({ knowledgeBases, onEdit, onDelete, onHistory }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const itemsPerPage = 10;

  const filteredKnowledgeBases = knowledgeBases?.filter(kb =>
    kb?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    kb?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    kb?.createdBy?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const totalPages = Math.ceil(filteredKnowledgeBases?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedKnowledgeBases = filteredKnowledgeBases?.slice(startIndex, startIndex + itemsPerPage);

  const handleDeleteClick = (kb) => {
    setDeleteConfirm(kb?.id);
  };

  const handleDeleteConfirm = (kb) => {
    onDelete?.(kb);
    setDeleteConfirm(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const getTypeBadge = (type) => {
    return type === 'Link' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary';
  };

  return (
    <>
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-error/10">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Confirm Deletion</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Are you sure you want to delete this knowledge base? All associated data will be permanently removed.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={handleDeleteCancel}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                fullWidth
                iconName="Trash2"
                onClick={() => {
                  const kb = knowledgeBases?.find(k => k?.id === deleteConfirm);
                  handleDeleteConfirm(kb);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Knowledge Base</h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              {filteredKnowledgeBases?.length} knowledge bases found
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="search"
              placeholder="Search knowledge bases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full sm:w-64 px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Knowledge Base Name</th>
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Type</th>
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Last Updated</th>
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Created By</th>
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Used By Agent(s)</th>
                <th className="text-left py-3 px-4 text-xs md:text-sm font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedKnowledgeBases?.map((kb) => (
                <tr key={kb?.id} className="border-b border-border hover:bg-muted/50 transition-base">
                  <td className="py-3 px-4">
                    <div className="flex items-start gap-2">
                      <Icon name="FileText" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm md:text-base font-medium text-foreground">
                          {kb?.name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs md:text-sm font-medium whitespace-nowrap ${getTypeBadge(kb?.type)}`}>
                      {kb?.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm md:text-base text-foreground whitespace-nowrap">
                    {kb?.lastUpdated}
                  </td>
                  <td className="py-3 px-4 text-sm md:text-base text-foreground">
                    {kb?.createdBy}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {kb?.usedByAgents?.slice(0, 2)?.map((agent, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-accent/10 text-accent rounded text-xs">
                          {agent}
                        </span>
                      ))}
                      {kb?.usedByAgents?.length > 2 && (
                        <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs">
                          +{kb?.usedByAgents?.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit?.(kb)}
                        className="p-1.5 hover:bg-muted rounded transition-base"
                        title="Edit knowledge base"
                      >
                        <Icon name="Edit" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(kb)}
                        className="p-1.5 hover:bg-muted rounded transition-base"
                        title="Delete knowledge base"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                      {/* <button
                        onClick={() => onHistory?.(kb)}
                        className="p-1.5 hover:bg-muted rounded transition-base"
                        title="View history"
                      >
                        <Icon name="History" size={16} className="text-muted-foreground" />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 md:mt-6">
            <p className="text-xs md:text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredKnowledgeBases?.length)} of {filteredKnowledgeBases?.length} knowledge bases
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-lg text-xs md:text-sm font-medium transition-base ${
                        currentPage === pageNum
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default KnowledgeBaseTable;